import { useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { GooglePay, PaymentData, SubmitActions, UIElement } from '@adyen/adyen-web';

import {
  ESdkEvents,
  PaymentMethod,
  EPaymentProvider,
  AppchargeErrorType,
  EBEPaymentMethodsName,
} from '../../../../../../../contracts';
import { sendFEEvent } from '../../../../../../../eventUtil';
import { useAPI } from '../../../../../../../services/api.service';
import { useCheckout } from '../../../../../../../hooks/use-checkout';
import { usePaymentState } from '../../../../../hooks/use-payment-state';
import { AdyenFinalizePaymentResponse } from '../../../../../../../contracts';
import { useAdyenCheckout } from '../../../../AdyenPaymentElement/hooks/use-adyen-checkout';
import { getPaymentRequestAmount } from '../../../../../../../utils/get-payment-request-amount';
import { generateAppchargeErrorMessage } from '../../../../../../../utils/generate-appcharge-error-message';
import { useOrderPolling } from '../../../../../hooks/use-order-polling';
import { apmLatencyTracker } from '../../../../../../../utils/apm-latency-tracker';

export function UseAdyenGooglePay(onReady: (isAvailable: boolean) => void) {
  const isAdyenPaymentInProgress = useRef(false);
  const [googlePay, setGooglePay] = useState<UIElement | null>(null);
  const submitParamsRef = useRef<{ email: string }>({ email: '' });
  const { checkoutData, getEventParams } = useCheckout();
  const setIsPaymentLoading = usePaymentState((state) => state.setIsPaymentLoading);
  const setErrorMessage = usePaymentState((state) => state.setErrorMessage);
  const priceBreakdown = usePaymentState((state) => state.priceBreakdown);
  const { startOrderPolling } = useOrderPolling();
  const { API } = useAPI();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const googlePayPM = useMemo(
    () =>
      checkoutData?.paymentMethods.find(
        (pm) =>
          pm.provider === EPaymentProvider.adyen &&
          pm.paymentMethodName === EBEPaymentMethodsName.google,
      ),
    [checkoutData?.paymentMethods],
  );
  const { checkoutInstance } = useAdyenCheckout(googlePayPM);

  const gatewayMerchantId = googlePayPM?.publicConfig?.merchantId || '';
  const configuration = googlePayPM?.paymentMethodConfig?.configuration || {};

  useEffect(() => {
    try {
      apmLatencyTracker.startTracking('Google Pay', googlePayPM?.id);
    } catch (error) {
      console.error('Failed to start APM latency tracking:', error);
    }

    let googlePay: GooglePay;
    if (checkoutInstance) {
      googlePay = new GooglePay(checkoutInstance, {
        callbackIntents: [],
        showPayButton: true,
        isExpress: true,
        configuration: {
          gatewayMerchantId,
          ...configuration,
        },
        onAuthorized: (data, actions) => {
          submitParamsRef.current.email =
            data.authorizedEvent.email || checkoutData?.customer.email || '';
          actions.resolve();
        },
        onError: (error) => {
          console.log('onError Google Pay', { error });
          handleError();
        },
        onPaymentCompleted,
        onPaymentFailed,
        onSubmit: async (state, _, actions) => {
          sendFEEvent(ESdkEvents.ON_PAYMENT_INTENT, {
            ...getEventParams(),
            paymentMethodName: googlePayPM?.paymentMethodDisplayName,
          });
          if (isAdyenPaymentInProgress.current) {
            return;
          }
          isAdyenPaymentInProgress.current = true;
          setIsPaymentLoading(true);
          try {
            const finalizePaymentResponse = await API.finalizePayment<AdyenFinalizePaymentResponse>(
              createGooglePayFinalizeBody(googlePayPM!, state.data, submitParamsRef.current.email),
            );
            handleServerResponse(finalizePaymentResponse, actions);
          } catch {
            handleError();
          }
        },

        buttonSizeMode: 'fill',
        buttonRadius: 5,
        emailRequired: true,
        transactionInfo: {
          displayItems: [],
          countryCode: checkoutData?.customer?.geoLocator?.countryCode2,
          currencyCode: checkoutData?.pricing.currencyCode,
          totalPriceStatus: 'FINAL',
          totalPrice: getPaymentRequestAmount(priceBreakdown?.priceTotal, checkoutData?.pricing),
          totalPriceLabel: 'Total',
        },
      });

      googlePay
        ?.isAvailable()
        .then(() => onReady?.(true))
        .catch(() => {
          setGooglePay(null);
          onReady?.(false);
        })
        .finally(() => {
          try {
            apmLatencyTracker.endTracking('Google Pay', googlePayPM?.id, {
              paymentMethodName: googlePayPM?.paymentMethodName,
              paymentMethodDisplayName: googlePayPM?.paymentMethodDisplayName,
              provider: 'Adyen',
              type: 'Express',
            });
          } catch (error) {
            console.error('Failed to end APM latency tracking:', error);
          }
        });

      setGooglePay(googlePay);
    }
  }, [checkoutInstance, priceBreakdown]);

  const onPaymentCompleted = () => {
    sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
      ...getEventParams(),
      paymentMethodName: googlePayPM?.paymentMethodDisplayName,
    });
    startOrderPolling(checkoutData?.orderId!);
  };

  const onPaymentFailed = () => {
    handleError();
  };

  const handleServerResponse = (
    finalizePaymentResponse: AdyenFinalizePaymentResponse,
    actions: SubmitActions,
  ) => {
    const { status, resultCode, action } = finalizePaymentResponse.adyen;
    if (status === 'succeeded') {
      actions.resolve({ resultCode: resultCode, action });
    } else if (status === 'failed') {
      handleError();
    }
  };

  const handleError = (appChargeErrorType?: AppchargeErrorType) => {
    const errorMessage = generateAppchargeErrorMessage(t, appChargeErrorType);
    sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
      ...getEventParams(),
      paymentMethodName: googlePayPM?.paymentMethodDisplayName,
    });
    setErrorMessage(errorMessage);
    setIsPaymentLoading(false);
    isAdyenPaymentInProgress.current = false;
  };

  return {
    googlePayRef: googlePay,
  };
}

function createGooglePayFinalizeBody(
  selectedPaymentMethod: PaymentMethod,
  adyenData: PaymentData,
  email: string,
) {
  return {
    paymentProvider: EPaymentProvider.adyen,
    receipt_email: email,
    paymentMethodDisplayName: selectedPaymentMethod.paymentMethodDisplayName,
    paymentMethodId: selectedPaymentMethod.id,
    params: {
      browserInfo: adyenData.browserInfo,
      paymentMethod: adyenData.paymentMethod,
    },
  };
}
